import {
  doc,
  getDoc,
  onSnapshot,
  type DocumentSnapshot,
} from "firebase/firestore";

import { db } from "integrations/firebase/firestore";

import { loadMemberships } from "../dashboard/use-my-memberships";
import type { LandingSources, ProvisioningRequest } from "./landing";

function toRequest(snapshot: DocumentSnapshot): ProvisioningRequest | null {
  if (!snapshot.exists()) return null;
  const data = snapshot.data();
  return {
    status: data.status,
    rosterClaimedAt: data.rosterClaimedAt,
    completedAtMs: toMillis(data.completedAt),
    requestedAtMs: toMillis(data.requestedAt),
  };
}

function toMillis(value: unknown): number | null {
  const timestamp = value as { toMillis?: () => number } | null | undefined;
  return typeof timestamp?.toMillis === "function"
    ? timestamp.toMillis()
    : null;
}

/** The Firestore reads behind `decideLanding`, for one signed-in person. */
export function firestoreLandingSources(uid: string): LandingSources {
  const requestRef = doc(db, "user_provisioning_requests", uid);
  return {
    membershipCount: async () => (await loadMemberships(uid)).length,
    request: async () => toRequest(await getDoc(requestRef)),
    profileExists: async () => (await getDoc(doc(db, "users", uid))).exists(),
    waitForRequest: (settled, capMs, signal) =>
      new Promise<void>((resolve) => {
        if (signal?.aborted) {
          resolve();
          return;
        }
        let done = false;
        const listening: { stop?: () => void } = {};
        const finish = () => {
          if (done) return;
          done = true;
          clearTimeout(timer);
          signal?.removeEventListener("abort", finish);
          listening.stop?.();
          resolve();
        };
        const timer = setTimeout(finish, capMs);
        signal?.addEventListener("abort", finish);
        const stop = onSnapshot(
          requestRef,
          (snapshot) => {
            if (settled(toRequest(snapshot))) finish();
          },
          finish,
        );
        // The first snapshot can arrive before onSnapshot has returned.
        if (done) stop();
        else listening.stop = stop;
      }),
    now: () => Date.now(),
  };
}
